/* Admin-only collapsible panel that hands the operator copy-paste-ready campaign
 * URLs so traffic for Cash Discount / Free POS / Savings Analysis always lands on
 * the right sub-landing page (never the regular content pages /cash-discount or
 * /free-pos-program).
 *
 * Lives inside /admin → naturally admin-only via the page's auth gating. */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Link as LinkIcon, ChevronDown, ChevronRight, Copy } from "lucide-react";

const SITE_ORIGIN = "https://swipetronics.com";

/* Canonical campaign landing pages — MUST match LANDING_PAGE_TO_CAMPAIGN in server.py. */
const CAMPAIGNS = [
  { key: "cash_discount", label: "Cash Discount", path: "/cash-discount-savings" },
  { key: "free_pos", label: "Free POS", path: "/free-pos-offer" },
  { key: "savings_analysis", label: "Savings Analysis", path: "/savings-analysis" },
];

/* Source presets. utm_source values match backend SOURCE_LABELS keys so the
 * resulting source_label renders pretty in the dashboard. */
const SOURCES = [
  { key: "facebook", label: "Facebook", utm_source: "facebook", utm_medium: "paid_social" },
  { key: "instagram", label: "Instagram", utm_source: "instagram", utm_medium: "paid_social" },
  { key: "tiktok", label: "TikTok", utm_source: "tiktok", utm_medium: "paid_social" },
  { key: "google_ads", label: "Google Ads", utm_source: "google", utm_medium: "cpc" },
  { key: "gbp", label: "Google Business Profile", utm_source: "google_business_profile", utm_medium: "local_listing" },
  { key: "youtube", label: "YouTube", utm_source: "youtube", utm_medium: "paid_social" },
  { key: "linkedin", label: "LinkedIn", utm_source: "linkedin", utm_medium: "paid_social" },
  { key: "x", label: "X / Twitter", utm_source: "x", utm_medium: "paid_social" },
  { key: "yelp", label: "Yelp", utm_source: "yelp", utm_medium: "local_listing" },
  { key: "referral", label: "Referral", utm_source: "referral", utm_medium: "referral" },
];

function buildUrl(source, campaign) {
  const params = new URLSearchParams({
    utm_source: source.utm_source,
    utm_medium: source.utm_medium,
    utm_campaign: campaign.key,
  });
  return `${SITE_ORIGIN}${campaign.path}?${params.toString()}`;
}

async function writeToClipboard(text) {
  if (navigator?.clipboard?.writeText) {
    try { await navigator.clipboard.writeText(text); return true; }
    catch (err) { console.warn("[utm-links] clipboard.writeText failed:", err); }
  }
  // Legacy fallback for older Safari / non-secure contexts.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (err) {
    console.warn("[utm-links] fallback copy failed:", err);
    return false;
  }
}

export default function RecommendedUtmLinks() {
  const [open, setOpen] = useState(false);
  const [expandedSource, setExpandedSource] = useState(null);
  const grid = useMemo(() => SOURCES.map((s) => ({
    source: s,
    links: CAMPAIGNS.map((c) => ({ campaign: c, url: buildUrl(s, c) })),
  })), []);

  const copy = async (url, label) => {
    const ok = await writeToClipboard(url);
    if (ok) toast.success(`Copied ${label}`);
    else toast.error("Copy failed — long-press / right-click to copy manually");
  };

  const copyAll = async () => {
    const header = "Source\tCampaign\tURL";
    const rows = grid.flatMap(({ source, links }) =>
      links.map(({ campaign, url }) => `${source.label}\t${campaign.label}\t${url}`)
    );
    const tsv = [header, ...rows].join("\n");
    const ok = await writeToClipboard(tsv);
    if (ok) toast.success(`Copied ${rows.length} URLs as TSV — paste into Google Sheets`);
    else toast.error("Copy failed — long-press / right-click to copy manually");
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-4" data-testid="utm-links-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="utm-links-toggle"
        className="w-full flex items-center justify-between px-5 py-3 text-left">
        <div className="flex items-center gap-2.5">
          <LinkIcon className="w-4 h-4 text-[var(--swipe-accent)]" />
          <div>
            <div className="text-[13px] font-extrabold text-[var(--swipe-navy)]">Recommended UTM Links</div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              Copy-ready campaign URLs · 10 sources × 3 promotions · never use /cash-discount or /free-pos-program for ads
            </div>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open ? (
        <div className="border-t border-slate-100 px-3 py-3 space-y-2" data-testid="utm-links-body">
          {grid.map(({ source, links }) => {
            const expanded = expandedSource === source.key;
            return (
              <div key={source.key} className="rounded-lg border border-slate-200">
                <button type="button" onClick={() => setExpandedSource(expanded ? null : source.key)}
                  data-testid={`utm-source-toggle-${source.key}`}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-[var(--swipe-navy)]">{source.label}</span>
                    <span className="text-[10.5px] text-slate-400">utm_source={source.utm_source} · utm_medium={source.utm_medium}</span>
                  </div>
                  {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </button>
                {expanded ? (
                  <div className="border-t border-slate-100 divide-y divide-slate-100">
                    {links.map(({ campaign, url }) => (
                      <div key={campaign.key} className="grid grid-cols-12 items-center gap-2 px-3 py-2">
                        <div className="col-span-3 sm:col-span-2 text-[12px] font-semibold text-[var(--swipe-accent)] uppercase tracking-wider">
                          {campaign.label}
                        </div>
                        <div className="col-span-7 sm:col-span-8 min-w-0">
                          <code className="block text-[11.5px] text-slate-700 font-mono truncate" title={url}
                            data-testid={`utm-url-${source.key}-${campaign.key}`}>
                            {url}
                          </code>
                        </div>
                        <div className="col-span-2 text-right">
                          <button type="button" onClick={() => copy(url, `${source.label} → ${campaign.label}`)}
                            data-testid={`utm-copy-${source.key}-${campaign.key}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)] text-[11px] font-semibold text-slate-600">
                            <Copy className="w-3 h-3" /> Copy
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
          <div className="flex items-center justify-between gap-3 pt-2 px-1">
            <div className="text-[11px] text-slate-400 leading-snug">
              Cash Discount → /cash-discount-savings · Free POS → /free-pos-offer · Savings Analysis → /savings-analysis.
              The regular content pages /cash-discount and /free-pos-program are intentionally NOT included.
            </div>
            <button type="button" onClick={copyAll} data-testid="utm-copy-all"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-[12px] font-semibold whitespace-nowrap">
              <Copy className="w-3.5 h-3.5" /> Copy All URLs
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
